import { useEffect } from 'react'
import { useLatest } from 'react-use'
import type { InitOptions } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor'
import { getWindow } from '@ztechapps/common.utils'
import { useChannelContext } from '../Channel'
import { ComposerModeEnum, useConversationContext } from './Context'

// The TinyMCE editor instance `setup` hands the caller — reads off the same `InitOptions` the
// editor itself is typed with instead of `any`.
type TinyEditor = Parameters<NonNullable<InitOptions['setup']>>[0]

export type UseComposerModeTintResult = (editor: TinyEditor) => void

// Tints the note body yellow so the two modes read apart while typing, without a hex value
// anywhere (webui-styling-style-guide.md §3): the pair is read off the editor's own computed
// style rather than written here. Declared and measured at
// libs/webui/data-tools/src/DataTable/datatable.module.scss:20-56 (webui/contrast.md).
const applyComposerModeTint = (editor: TinyEditor, isNote: boolean) => {
  if (!isNote) {
    editor.dom.setStyles(editor.getBody(), { 'background-color': '', color: '' })
    return
  }
  const computed = getWindow().getComputedStyle(editor.getContainer())
  editor.dom.setStyles(editor.getBody(), {
    'background-color': computed?.getPropertyValue('--bs-warning-bg-subtle'),
    color: computed?.getPropertyValue('--bs-warning-text-emphasis'),
  })
}

// Keeps the editor body tinted for the current reply mode. Returns the init-time applier: the
// editor's `init` prop is read once, so a composer calls it from its own `init` handler and the
// tint then follows the mode held in the ref, not the mode of the render that built the editor.
export const useComposerModeTint = (): UseComposerModeTintResult => {
  const { composerMode } = useConversationContext()
  const { chatInputRef } = useChannelContext()
  const isNote = composerMode === ComposerModeEnum.Note
  const isNoteRef = useLatest(isNote)

  useEffect(() => {
    if (chatInputRef.current?.initialized && !chatInputRef.current.removed) {
      applyComposerModeTint(chatInputRef.current, isNote)
    }
  }, [isNote, chatInputRef])

  return (editor: TinyEditor) => applyComposerModeTint(editor, isNoteRef.current)
}
